import { ampli } from '@/ampli';
import { useState } from 'react';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';
import { TokenPriceChart } from '@/components/TokenPriceChart';
import { SwitchButton } from '@/components/TokenProfile/SwitchButton';
import { TokenProfileContractAddress } from '@/components/TokenProfile/TokenProfileContractAddress';
import { TokenSharingModal } from '@/components/TokenProfile/TokenSharingModal';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ExternalLinkIcon } from '@/ui/Icons/ExternalLinkIcon';
import { GlobeIcon } from '@/ui/Icons/GlobeIcon';
import { ShareIcon } from '@/ui/Icons/ShareIcon';
import { SmallDotIcon } from '@/ui/Icons/SmallDotIcon';
import { TwitterIcon } from '@/ui/Icons/TwitterIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import { Panel } from '@/ui/Panel';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { getBlockExplorerTokenURL } from '@/utils/blockExplorers';
import { classNames } from '@/utils/classnames';
import { primitiveColors } from '@/utils/generated/primitiveColors';
import { isTokenResultNativeAsset } from '@/utils/multichain';
import { tokenKey } from '@/utils/normalizeAddress';

import { TokenPanelStatCells } from './TokenPanelStatCells';
import { TokenPanelStats } from './TokenPanelStats';
import { useLimitPriceLine } from './useLimitPriceLine';

const CHART_HEIGHT = 338;

const iconButtonStyles = buttons({
  color: 'transparent',
  shape: 'roundedSubtle',
  size: 'extraSmall',
  iconOnly: true,
  class: 'size-7 shrink-0 p-0 text-token-gray-700 hover:text-token-gray-1600',
});

// Matches the 40px avatar row on phones and the 48px stat cells on tablet+, so the icons
// centre on whichever sits beside them.
const actionsClassName = 'flex h-10 tablet:h-12 shrink-0 items-center';

export type TokenPanelSide = 'sell' | 'buy';

type TokenPanelProps = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  side: TokenPanelSide;
  onSideChange: (side: TokenPanelSide) => void;
  chartHeight?: number;
  /** The bottom sheet is opened for one side already, so it hides the switch. */
  showSwitchButton?: boolean;
  embedded?: boolean;
  active?: boolean;
  className?: string;
};

export const TokenPanel = ({
  sellToken,
  buyToken,
  side,
  onSideChange,
  chartHeight = CHART_HEIGHT,
  showSwitchButton = true,
  embedded = false,
  active = true,
  className,
}: TokenPanelProps) => {
  const token = side === 'buy' ? buyToken : sellToken;
  const otherToken = side === 'buy' ? sellToken : buyToken;
  const [checkoutMode] = useCheckoutMode();
  const [advancedOrderType] = useAdvancedOrderType();
  const [shareOpenTime, setShareOpenTime] = useState(0);
  const [isShareOpen, setShareOpen] = useState(false);
  const { priceUsd: limitPriceUsd, setPriceUsd: setLimitPriceUsd } = useLimitPriceLine({
    sellToken,
    buyToken,
    side,
    enabled: active && checkoutMode === CheckoutModes.advanced,
  });

  const metadataAddress = active ? token?.address : undefined;
  const { data: tokenInfo } = useTokenInfo(metadataAddress, token?.chainId);
  // CoinGecko sometimes prefixes the handle with `@`; anything else non-handle-like is dropped.
  const twitterScreenName = tokenInfo?.info?.links?.twitter_screen_name?.replace(/^@/, '');
  const twitterHref =
    twitterScreenName && /^[A-Za-z0-9_]{1,15}$/.test(twitterScreenName)
      ? `https://x.com/${twitterScreenName}`
      : undefined;
  // CoinGecko pads `homepage` with empty strings.
  const websiteHref = tokenInfo?.info?.links?.homepage?.find((url) => /^https?:\/\//i.test(url));

  const showTokenAddress = !!token && !isTokenResultNativeAsset(token);
  const explorerHref = showTokenAddress
    ? getBlockExplorerTokenURL(token.chainId, token.address)
    : undefined;
  const Container = embedded ? 'div' : Panel;

  const handleShare = () => {
    if (!token) return;
    setShareOpenTime(Date.now());
    setShareOpen(true);
    ampli.tokenShareClicked({
      token_address: token.address,
      token_chain_id: token.chainId,
      token_chain_name: token.chainName,
      token_symbol: token.symbol,
    });
  };

  return (
    <Container className={classNames('flex flex-col gap-3 py-4', className)}>
      {/* On tablet+ the stat cells sit between the website/X links and share; on phones they
          collapse to icon-labelled stats beside the chips. */}
      <div className="flex items-start gap-3 px-3 tablet:px-6">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {token ? (
              <TokenAvatar address={token.address} chainId={token.chainId} size={40} />
            ) : (
              <AvatarSkeleton size={40} />
            )}

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {token ? (
                <span
                  className={fontStyles({
                    variant: 'H5',
                    class: 'min-w-0 truncate',
                  })}
                >
                  {token.name}
                </span>
              ) : (
                <TextSkeleton width={110} height={22} />
              )}

              {token ? (
                <div
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: 'flex min-w-0 items-center text-token-gray-700 leading-[20px]',
                  })}
                >
                  <span className="min-w-0 truncate">{token.symbol}</span>
                  {showTokenAddress && (
                    <>
                      <SmallDotIcon className="ml-1 shrink-0 text-token-gray-500" />
                      {/* The button's own padding is trimmed onto the dot's rhythm. */}
                      <TokenProfileContractAddress token={token} className="-ml-1 shrink-0" />
                      {explorerHref && (
                        <ExternalLink
                          className="-ml-1.5 flex shrink-0 items-center rounded-sm text-token-gray-700 transition-colors focus-visible:outline-token-gray-500 tablet:hover:text-token-gray-900"
                          href={explorerHref}
                        >
                          <span className="sr-only">{`View ${token.symbol} on block explorer`}</span>
                          <ExternalLinkIcon className="ml-0.5 size-3.5" />
                        </ExternalLink>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <TextSkeleton width={80} height={20} />
              )}
            </div>
          </div>

          {/* Always rendered: `min-h-6` matches the tallest chip so the chart below
              keeps its position while the token and its stats resolve. */}
          <div className="flex min-h-6 flex-wrap items-center gap-1">
            {!token && <TextSkeleton width={180} height={16} />}
            {token && active && (
              <>
                <TokenPanelStats key={tokenKey(token.address, token.chainId)} token={token} />
                <TokenPanelStatCells
                  token={token}
                  variant="inline"
                  className="flex tablet:hidden"
                />
              </>
            )}
          </div>
        </div>

        {token && (websiteHref || twitterHref) && (
          <div className={actionsClassName}>
            {websiteHref && (
              <ExternalLink className={iconButtonStyles} href={websiteHref}>
                <span className="sr-only">{`${token.symbol} website`}</span>
                <GlobeIcon aria-hidden className="size-4" />
              </ExternalLink>
            )}
            {twitterHref && (
              <ExternalLink className={iconButtonStyles} href={twitterHref}>
                <span className="sr-only">{`${token.symbol} on X`}</span>
                <TwitterIcon aria-hidden className="size-3.5" />
              </ExternalLink>
            )}
          </div>
        )}

        {token && active && <TokenPanelStatCells token={token} className="hidden tablet:flex" />}

        {token && (
          <div className={actionsClassName}>
            <button
              aria-label={`Share ${token.symbol}`}
              className={iconButtonStyles}
              onClick={handleShare}
              type="button"
            >
              <ShareIcon aria-hidden className="size-4" />
            </button>
          </div>
        )}
      </div>

      {/* The chart's price summary is pulled 8px above its box, so the wrapper
          gives it back and the identity, stats and price rows keep one rhythm. */}
      <div className="mt-2">
        <TokenPriceChart
          token={token}
          showChartTypeSelectorOverlay
          height={chartHeight}
          showLabels
          showVolume
          showTimeframes
          showTokenPrice
          showMetricSelector
          enableCandlestickView
          autoRefresh
          showTradeMarkers
          priceLineUsd={limitPriceUsd}
          onPriceLineChange={setLimitPriceUsd}
          priceLineLabel={ADVANCED_ORDER_TYPE_LABELS[advancedOrderType]}
          priceLineColor={
            primitiveColors[advancedOrderType === 'stop-loss' ? 'matcha-red' : 'matcha-green']
          }
          active={active}
          bottomBarStart={
            showSwitchButton && otherToken ? (
              <SwitchButton
                activeToken={token}
                inactiveToken={otherToken}
                onTokenSelect={() => onSideChange(side === 'buy' ? 'sell' : 'buy')}
              />
            ) : undefined
          }
        />
      </div>

      {token && (
        <TokenSharingModal
          address={token.address}
          chainId={token.chainId}
          symbol={token.symbol}
          openTime={shareOpenTime}
          isOpen={isShareOpen}
          handleOpenChange={setShareOpen}
        />
      )}
    </Container>
  );
};
